import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { collectFrameGroups } from "@/appInterface/windows/ExportWindow/utils";
import { GifWidget } from "@/elements/gif";
import { useFramePreviews } from "@/interface/RightSheet/Presentations/PresentationsNavigator/PresentationView/hooks/useFramePreviews";
import type { PresentationModeData } from "@/interface/RightSheet/Presentations/PresentationsNavigator/types";

type PresentationWithPreviews = PresentationModeData & {
    previews: Record<string, ImageBitmap>;
    hasAnimation: boolean;
};

export const usePresentation = (
    basePresentation: PresentationModeData | null,
    frameIndex?: number,
    pollingEnabled?: boolean,
): PresentationWithPreviews | null => {
    const framesForPreview = useMemo(() => {
        if (!basePresentation?.frames?.length) {
            return [];
        }
        if (typeof frameIndex === "number") {
            const frame = basePresentation.frames[frameIndex];
            return frame ? [frame] : [];
        }
        return basePresentation.frames;
    }, [basePresentation, frameIndex]);

    const hasAnimation = useMemo(() => {
        const parent = framesForPreview[0]?.parent;
        if (!parent) {
            return false;
        }

        const { groupsWithoutFrame } = collectFrameGroups([parent]);
        return groupsWithoutFrame.some(
            (group) => group.isItemWrapper() && group.children[0] instanceof GifWidget,
        );
    }, [framesForPreview]);

    const generatePreviews = useFramePreviews(framesForPreview, "bitmap");

    const [presentation, setPresentation] = useState<PresentationWithPreviews | null>(null);

    const previewsRef = useRef<Record<string, ImageBitmap>>({});

    const cleanupBitmaps = useCallback((bitmaps: Record<string, ImageBitmap>) => {
        Object.values(bitmaps).forEach((bitmap) => {
            try {
                bitmap.close();
            } catch {
                // detached bitmap может уже быть закрыт
            }
        });
    }, []);

    useEffect(() => {
        if (!basePresentation) return;

        const shouldPoll = pollingEnabled || hasAnimation;
        let cancelled = false;
        let rafId: number;
        let lastUpdate = 0;
        let inFlight = false;

        const updatePreviews = async () => {
            if (inFlight) return;

            inFlight = true;
            try {
                const previews = await generatePreviews();

                if (cancelled) {
                    cleanupBitmaps(previews);
                    return;
                }

                const previousPreviews = previewsRef.current;
                previewsRef.current = previews;

                setPresentation((prev) => {
                    return {
                        ...(prev ?? basePresentation),
                        ...basePresentation,
                        previews,
                        hasAnimation,
                    };
                });

                // очень аккуратно очищаем старые bitmap, есть вероятность что UI их может еще использовать в отрисовке
                if (Object.keys(previousPreviews).length) {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            cleanupBitmaps(previousPreviews);
                        });
                    });
                }
            } finally {
                inFlight = false;
            }
        };

        const loop = (time: number) => {
            if (cancelled) return;

            if (time - lastUpdate >= 100) {
                lastUpdate = time;
                void updatePreviews();
            }

            rafId = requestAnimationFrame(loop);
        };

        void updatePreviews();

        if (shouldPoll) {
            rafId = requestAnimationFrame(loop);
        }

        return () => {
            cancelled = true;
            if (rafId) {
                cancelAnimationFrame(rafId);
            }

            const currentPreviews = previewsRef.current;
            previewsRef.current = {};

            if (Object.keys(currentPreviews).length) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        cleanupBitmaps(currentPreviews);
                    });
                });
            }
        };
    }, [basePresentation, generatePreviews, pollingEnabled, hasAnimation, cleanupBitmaps]);

    return presentation;
};
